import { ApiClient, ApiResponse } from '../client';
import { Category } from '../models';

/**
 * Servicio para gestionar categorías
 */
export class CategoryService {
  private client: ApiClient;
  
  constructor(client: ApiClient) {
    this.client = client;
  }
  
  /**
   * Obtener todas las categorías
   */
  public async getCategories(): Promise<ApiResponse<Category[]>> {
    return await this.client.get<Category[]>('/categories', {
      useCache: true,
      cacheTTL: 30 * 60 * 1000 // 30 minutos de caché
    });
  }
  
  /**
   * Obtener una categoría por su ID
   * @param id ID de la categoría
   */
  public async getCategory(id: string): Promise<ApiResponse<Category>> {
    return await this.client.get<Category>(`/categories/${id}`, {
      useCache: true
    });
  }
  
  /**
   * Obtener una categoría por su slug
   * @param slug Slug de la categoría
   */
  public async getCategoryBySlug(slug: string): Promise<ApiResponse<Category>> {
    return await this.client.get<Category>('/categories', {
      params: { slug },
      useCache: true
    });
  }
  
  /**
   * Obtener categorías hijas de una categoría padre
   * @param parentId ID de la categoría padre
   */
  public async getSubcategories(parentId: string): Promise<ApiResponse<Category[]>> {
    return await this.client.get<Category[]>('/categories', {
      params: { parent: parentId },
      useCache: true
    });
  }
  
  /**
   * Obtener categorías hijas o raíces (parent = null)
   */
  public async getChildren(parentId: string | null): Promise<ApiResponse<Category[]>> {
    const params: Record<string, any> = {};
    params.parent = parentId === null ? 'null' : parentId;
    return await this.client.get<Category[]>('/categories', { params, useCache: true, cacheTTL: 10 * 60 * 1000 });
  }

  /**
   * Obtener árbol completo de categorías
   */
  public async getTree(): Promise<ApiResponse<any>> {
    return await this.client.get<any>('/categories/tree', { useCache: true, cacheTTL: 10 * 60 * 1000 });
  }

  /**
   * Obtener breadcrumbs por ID de categoría
   */
  public async getBreadcrumbs(id: string): Promise<ApiResponse<Array<{ id: string; name: string; slug: string }>>> {
    return await this.client.get(`/categories/${id}/breadcrumbs`, { useCache: true, cacheTTL: 10 * 60 * 1000 });
  }
  
  /**
   * Obtener categorías principales (sin padre)
   */
  public async getRootCategories(): Promise<ApiResponse<Category[]>> {
    return await this.client.get<Category[]>('/categories', {
      params: { root: true },
      useCache: true
    });
  }
  
  /**
   * Crear una nueva categoría (admin)
   * @param categoryData Datos de la categoría
   */
  public async createCategory(categoryData: Partial<Category>): Promise<ApiResponse<Category>> {
    return await this.client.post<Category>('/categories', categoryData);
  }
  
  /**
   * Actualizar una categoría existente (admin)
   * @param id ID de la categoría
   * @param categoryData Datos actualizados
   */
  public async updateCategory(id: string, categoryData: Partial<Category>): Promise<ApiResponse<Category>> {
    return await this.client.put<Category>(`/categories/${id}`, categoryData);
  }
  
  /**
   * Eliminar una categoría (admin)
   * @param id ID de la categoría
   */
  public async deleteCategory(id: string): Promise<ApiResponse<null>> {
    return await this.client.delete<null>(`/categories/${id}`);
  }
  
  /**
   * Subir una imagen para una categoría (admin)
   * @param categoryId ID de la categoría
   * @param imageFile Archivo de imagen
   */
  public async uploadCategoryImage(categoryId: string, imageFile: File): Promise<ApiResponse<Category>> {
    // Crear un FormData para enviar el archivo
    const formData = new FormData();
    formData.append('file', imageFile);
    
    // Usar la API de Fetch directamente para manejar formData
    const url = `${this.client.getAuthToken() ? '?token=' + this.client.getAuthToken() : ''}`;
    
    return await this.client.post<Category>(`/categories/${categoryId}/image${url}`, formData, {
      // No establecer Content-Type para que el navegador lo configure con el boundary correcto
      headers: {
        // Eliminar el Content-Type que se establece automáticamente
      }
    });
  }
  
  /**
   * Obtener productos de una categoría específica
   * @param categoryId ID de la categoría
   */
  public async getCategoryProducts(categoryId: string): Promise<ApiResponse<any>> {
    return await this.client.get<any>(`/categories/${categoryId}/products`, {
      useCache: true
    });
  }
} 